import React from "react";
import { Grid, MenuItem, TextField, Typography } from "@mui/material";

export const PAGE_CONTENT_LANGUAGES = [
  { code: "en", label: "English — default/fallback" },
  { code: "hn", label: "Hinglish" },
  { code: "hi", label: "Hindi" },
  { code: "bn", label: "Bengali" },
  { code: "gu", label: "Gujarati" },
  { code: "kn", label: "Kannada" },
  { code: "mr", label: "Marathi" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
];

const emptyContent = { title: "", text: "" };

export const getPageContentByLanguage = (item = {}) => Object.fromEntries(
  PAGE_CONTENT_LANGUAGES.map(({ code }) => {
    const content = code === "en" ? item : item.pageContentTranslations?.[code] || {};
    return [code, {
      title: content.pageContentTitle || "",
      text: content.pageContentText || "",
    }];
  })
);

export const appendPageContent = (payload, {
  pageName = "",
  entityId = "",
  language = "en",
  title = "",
  text = "",
} = {}) => {
  payload.append("pageContentTitle", title);
  payload.append("pageContentText", text);
  if (pageName === "seo" && entityId) payload.append("pageContentLanguage", language);
};

export const getPageContentRequestPayload = (payload, options = {}) => {
  const { pageName = "", entityId = "", language = "en", metaTitle = "", metaDescription = "" } = options;
  if (pageName !== "seo" || !entityId) return payload;

  const seoPayload = new FormData();
  if (language === "en") {
    seoPayload.append("metaTitle", metaTitle);
    seoPayload.append("metaDescription", metaDescription);
  }
  appendPageContent(seoPayload, options);
  return seoPayload;
};

export const hasUsableContent = (value = "") => value
  .replace(/<[^>]*>/g, "")
  .replace(/&nbsp;|\s/g, "")
  .length > 0;

export const switchPageContentLanguage = ({
  language = "en",
  nextLanguage = "en",
  storedContent = {},
  title = "",
  text = "",
  titleField = "",
  textField = "",
  setFieldValue = () => {},
  onLanguageChange = () => {},
  confirmDiscard = () => true,
} = {}) => {
  const saved = storedContent[language] || emptyContent;
  const isDirty = title !== saved.title || text !== saved.text;
  if (isDirty && !confirmDiscard()) return false;

  const next = storedContent[nextLanguage] || emptyContent;
  setFieldValue(titleField, next.title);
  setFieldValue(textField, next.text);
  onLanguageChange(nextLanguage);
  return true;
};

const PageContentLanguageEditor = ({
  language = "en",
  storedContent = {},
  title = "",
  text = "",
  titleField = "",
  textField = "",
  setFieldValue = () => {},
  onLanguageChange = () => {},
}) => {
  const handleChange = (event) => switchPageContentLanguage({
    language,
    nextLanguage: event.target.value,
    storedContent,
    title,
    text,
    titleField,
    textField,
    setFieldValue,
    onLanguageChange,
    confirmDiscard: () => window.confirm("Discard unsaved page-content changes?"),
  });

  const isMissing = language !== "en" && (!hasUsableContent(title) || !hasUsableContent(text));

  return (
    <>
      <Grid item xs={12}>
        <TextField
          select
          fullWidth
          size="small"
          label="Page content language"
          value={language}
          onChange={handleChange}
        >
          {PAGE_CONTENT_LANGUAGES.map(({ code, label }) => (
            <MenuItem key={code} value={code}>{label}</MenuItem>
          ))}
        </TextField>
      </Grid>
      {isMissing && (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            Translation missing; English will be displayed as fallback.
          </Typography>
        </Grid>
      )}
    </>
  );
};

export default PageContentLanguageEditor;
